import { useMemo } from "react"
import styled from "styled-components"

import { Badge, Select } from "@minuthq/meatball-ui-react"

import { paradiseLLMPromptCategorySelectOptions } from "src/components/Paradise/ParadiseLLMPrompts/paradiseLLMPromptCategorySelectOptions"
import { ParadiseLLMPromptsFilters } from "src/components/Paradise/ParadiseLLMPrompts/ParadiseLLMPromptsFilter"
import {
  TParadiseLLMFilters,
  TSortedParadiseLLMFilters,
} from "src/components/Paradise/ParadiseLLMPrompts/useParadiseLLMPromptsFilter"
import { ParadisePager } from "src/components/Paradise/ParadisePager"
import { ParadiseTable } from "src/components/Paradise/ParadiseTable"
import { Ellipsis, TopWrapper } from "src/components/Paradise/sharedStyles"
import {
  TParadiseLLMPrompt,
  TParadiseLLMPromptCategory,
} from "src/data/paradise/paradiseLLMPrompts/types/paradiseLLMPromptsQueryTypes"
import { Routes } from "src/router/routes"
import { useRouter } from "src/router/useRouter"
import { TSetSearchParamsProp } from "src/router/useSearchParams"
import { DropdownMultiSelect } from "src/ui/DropdownSelect/DropdownMultiSelect"
import { ColumnPopoverWrapper } from "src/ui/GridTable/useTableColumns/tableColumnStyles"
import {
  TableColumn,
  TTableSort,
  useTableColumns,
} from "src/ui/GridTable/useTableColumns/useTableColumns"
import FilterIcon from "src/ui/icons/calibrating.svg"
import { InternalLink } from "src/ui/Link/InternalLink"
import { formatUtcDate } from "src/utils/l10n"

const TABLE_STORAGE_KEY = "paradise_llm_table_columns"

type TParadiseLLMTableColumnValues =
  | "id"
  | "name"
  | "category"
  | "description"
  | "created_at"
  | "updated_at"
  | "versions"
  | "created_by"

interface ParadiseLLMTableProps {
  prompts: TParadiseLLMPrompt[]
  offset: number
  limit: number
  setOffset: (offset: number) => void
  filter: TParadiseLLMFilters
  setFilter: TSetSearchParamsProp<TParadiseLLMFilters>
  sortedFilters: TSortedParadiseLLMFilters
  sort?: TTableSort
  setSort?: (sort: TTableSort) => void
  totalCount?: number
  isLoading?: boolean
  isLoadingMore?: boolean
  error?: {
    hasError: boolean
    title: React.ReactNode
    description?: React.ReactNode
  }
}

export function ParadiseLLMPromptsTable({
  prompts,
  offset,
  limit,
  setOffset,
  filter,
  setFilter,
  sortedFilters,
  sort,
  setSort,
  totalCount,
  isLoading,
  isLoadingMore,
  error,
}: ParadiseLLMTableProps) {
  const { navigate } = useRouter()

  const tableColumns: TableColumn<
    TParadiseLLMPrompt,
    TParadiseLLMTableColumnValues
  >[] = useMemo(
    () => [
      {
        label: "ID",
        value: "id",
        enableSort: true,
        columnWidth: "min-content",
        renderCell: (data) => (
          <InternalLink to={Routes.ParadiseLLMPrompt.location(data.id)}>
            {data.id}
          </InternalLink>
        ),
      },
      {
        label: "Name",
        value: "name",
        enableSort: true,
        columnWidth: "min-content",
        renderCell: (data) => <Ellipsis>{data.name}</Ellipsis>,
      },
      {
        label: "Category",
        value: "category",
        enableSort: true,
        columnWidth: "min-content",
        renderCell: (data) => (
          <div>
            <MultiItemGrid>
              <div>{data.category}</div>
              {data.default && (
                <Badge color="info" size="x-small">
                  Default in category
                </Badge>
              )}
            </MultiItemGrid>
          </div>
        ),
        popoverContent: (
          <ColumnPopoverWrapper>
            <Select
              label="Category"
              value={filter.llm_prompts_category ?? ""}
              onChange={(v) =>
                setFilter(
                  "llm_prompts_category",
                  v as TParadiseLLMPromptCategory
                )
              }
              options={paradiseLLMPromptCategorySelectOptions}
            />
          </ColumnPopoverWrapper>
        ),
        popoverIcon: FilterIcon,
      },
      {
        label: "Description",
        value: "description",
        columnWidth: "min-content",
        renderCell: (data) => <Ellipsis>{data.description || "-"}</Ellipsis>,
      },
      {
        label: "# of versions",
        value: "versions",
        columnWidth: "min-content",
        renderCell: (data) => <div>{data.versions.length}</div>,
      },
      {
        label: "Created by",
        value: "created_by",
        columnWidth: "min-content",
        renderCell: (data) => (
          <InternalLink to={Routes.ParadiseUser.location(data.created_by)}>
            {data.created_by}
          </InternalLink>
        ),
      },
      {
        label: "Created",
        value: "created_at",
        enableSort: true,
        columnWidth: "min-content",
        renderCell: (data) => (
          <div>{formatUtcDate({ date: data.created_at })}</div>
        ),
      },
      {
        label: "Updated",
        value: "updated_at",
        enableSort: true,
        columnWidth: "min-content",
        renderCell: (data) => (
          <div>{formatUtcDate({ date: data.updated_at })}</div>
        ),
      },
    ],
    [filter, setFilter]
  )

  const {
    headerElements,
    rows,
    interactiveColumns,
    updateColumnVisibility,
    interactiveVisibleColumns,
    templateColumns,
  } = useTableColumns<TParadiseLLMPrompt>({
    columns: tableColumns,
    data: prompts,
    sort,
    onSortChange: setSort,
    options: { localStorageKey: TABLE_STORAGE_KEY },
  })

  return (
    <div>
      <TopWrapper>
        <ParadiseLLMPromptsFilters
          sortedFilters={sortedFilters}
          setFilter={setFilter}
        />

        <DropdownMultiSelect
          label="Columns"
          selectedValues={interactiveVisibleColumns.map((c) => c.value)}
          options={interactiveColumns}
          onChange={({ checked, option }) => {
            updateColumnVisibility(option.value, !checked)
          }}
        />
      </TopWrapper>

      <ParadiseTable
        header={headerElements}
        rows={rows ?? []}
        templateColumns={templateColumns}
        error={error}
        isLoading={isLoading}
        isLoadingMore={isLoadingMore}
        onRowClick={(index) => {
          const prompt = prompts[index]

          if (prompt) {
            navigate(Routes.ParadiseLLMPrompt.location(prompt.id))
          }
        }}
      />

      <ParadisePager
        offset={offset}
        limit={limit}
        totalCount={totalCount}
        setOffset={setOffset}
      />
    </div>
  )
}

const MultiItemGrid = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 1ch;
`
